import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import TurnstileWidget from '../components/TurnstileWidget';
import SEO from '../components/SEO';
import { trackEvent } from '../lib/metaPixel';

const SUBJECTS = ['contact.form.subjects.general', 'contact.form.subjects.services', 'contact.form.subjects.partnership', 'contact.form.subjects.support', 'contact.form.subjects.other'];

const Contact = () => {
  const { t } = useTranslation();
  const turnstileRef = useRef(null);

  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [turnstileToken, setTurnstileToken] = useState('');
  const [status, setStatus] = useState('idle'); // idle | success

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!turnstileToken) return;

    const subject = encodeURIComponent(form.subject || t('contact.form.subjects.general'));
    const body = encodeURIComponent(
      `Name: ${form.name}\nEmail: ${form.email}${form.phone ? `\nPhone: ${form.phone}` : ''}\n\n${form.message}`
    );

    // Automate Trustpilot review invitation
    if (typeof window.tp === 'function') {
      window.tp('createInvitation', {
        recipientEmail: form.email,
        recipientName: form.name,
        referenceId: `Contact_${Date.now()}`,
        source: 'InvitationScript',
        productSkus: ['sku_1', 'sku_2'],
        products: [
          {
            sku: 'sku_1',
            productUrl: 'https://csl-brands.com',
            imageUrl: 'https://csl-brands.com/favicon.png',
            name: form.subject || 'General Inquiry',
          }
        ],
        tags: ['ContactForm'],
        locationId: 'location_1',
        templateId: 'invitation_template_GUID',
      });
    }

    const [firstName, ...rest] = (form.name || '').trim().split(/\s+/);
    trackEvent('Contact', {
      userData: {
        email: form.email,
        phone: form.phone,
        firstName,
        lastName: rest.join(' '),
      },
      customData: { content_name: 'contact-form' },
    });

    window.location.href = `mailto:contact@csl-brands.com?subject=${subject}&body=${body}`;

    setStatus('success');
    setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    setTurnstileToken('');
    turnstileRef.current?.reset();
  };

  const isSubmitDisabled = !turnstileToken || !form.name || !form.email || !form.message;

  return (
    <div className="min-h-screen bg-white font-futura">
      <SEO
        path="/contact"
        title={`${t('contact.title')} | CSL Brands`}
        description={t('contact.subtitle')}
        image="/CSL-Brands-Integrated-Business-Solutions.jpg"
        keywords="contact CSL Brands, CSL Brands Douala, WhatsApp CSL Brands, demande de devis, get a quote, support, contact@csl-brands.com"
        structuredData={{
          '@type': 'ContactPage',
          name: t('contact.title'),
          description: t('contact.subtitle'),
          url: 'https://csl-brands.com/contact',
        }}
      />
      {/* ── Hero ── */}
      <section className="relative pt-24 pb-16 sm:pt-32 sm:pb-20 overflow-hidden bg-gradient-to-br from-gray-900 via-gray-800 to-csl-dark-green">
        <div className="absolute -top-20 -right-20 w-72 h-72 bg-csl-green/20 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 -left-16 w-64 h-64 bg-white/5 rounded-full blur-2xl pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-csl-green/20 text-csl-light-green text-sm font-semibold mb-6">
            <ChatBubbleLeftRightIcon className="w-4 h-4" />
            {t('contact.badge')}
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 leading-tight">
            {t('contact.title')}
          </h1>
          <p className="max-w-2xl mx-auto text-gray-300 text-base sm:text-lg leading-relaxed">
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* ── Contact info cards ── */}
      <section className="py-12 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            {/* Email */}
            <a
              href="mailto:contact@csl-brands.com"
              className="flex items-center gap-4 p-5 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md hover:border-csl-green/30 transition-all duration-300 group"
            >
              <div className="w-12 h-12 rounded-xl bg-csl-green/10 flex items-center justify-center flex-shrink-0 group-hover:bg-csl-green/20 transition-colors">
                <EnvelopeIcon className="w-6 h-6 text-csl-green" />
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('contact.info.email')}</p>
                <p className="text-sm font-medium text-gray-800 mt-0.5">contact@csl-brands.com</p>
              </div>
            </a>

            {/* WhatsApp */}
            <a
              href="https://wa.me/237653459638"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent('Contact', { customData: { content_name: 'whatsapp-cta' } })}
              className="flex items-center gap-4 p-5 bg-white rounded-2xl shadow-sm border border-gray-100 hover:shadow-md hover:border-csl-green/30 transition-all duration-300 group"
            >
              <div className="w-12 h-12 rounded-xl bg-csl-green/10 flex items-center justify-center flex-shrink-0 group-hover:bg-csl-green/20 transition-colors">
                <PhoneIcon className="w-6 h-6 text-csl-green" />
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('contact.info.whatsapp')}</p>
                <p className="text-sm font-medium text-gray-800 mt-0.5">+237 653 459 638</p>
              </div>
            </a>

            {/* Location */}
            <div className="flex items-center gap-4 p-5 bg-white rounded-2xl shadow-sm border border-gray-100">
              <div className="w-12 h-12 rounded-xl bg-csl-green/10 flex items-center justify-center flex-shrink-0">
                <MapPinIcon className="w-6 h-6 text-csl-green" />
              </div>
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('contact.info.location')}</p>
                <p className="text-sm font-medium text-gray-800 mt-0.5">Douala, Cameroun</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Main form section ── */}
      <section className="py-12 sm:py-16 lg:py-20 bg-gray-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Form header */}
            <div className="bg-gradient-to-r from-csl-green to-csl-dark-green px-6 sm:px-8 py-6">
              <h2 className="text-xl sm:text-2xl font-bold text-white">{t('contact.form.title')}</h2>
              <p className="text-white/80 text-sm mt-1">{t('contact.form.subtitle')}</p>
            </div>

            {/* Success state */}
            {status === 'success' ? (
              <div className="px-6 sm:px-8 py-16 flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
                  <CheckCircleIcon className="w-9 h-9 text-csl-green" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{t('contact.form.successTitle')}</h3>
                <p className="text-gray-600 text-sm max-w-sm">{t('contact.form.successMessage')}</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-6 px-6 py-2.5 bg-csl-green text-white font-semibold rounded-xl hover:bg-csl-dark-green transition-colors text-sm"
                >
                  {t('contact.form.sendAnother')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="px-6 sm:px-8 py-8 space-y-5">
                {/* Name + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                      {t('contact.form.name')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      required
                      placeholder={t('contact.form.namePlaceholder')}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-csl-green/30 focus:border-csl-green transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                      {t('contact.form.email')} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder={t('contact.form.emailPlaceholder')}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-csl-green/30 focus:border-csl-green transition-all"
                    />
                  </div>
                </div>

                {/* Phone + Subject */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                      {t('contact.form.phone')}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder={t('contact.form.phonePlaceholder')}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-csl-green/30 focus:border-csl-green transition-all"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                      {t('contact.form.subject')}
                    </label>
                    <select
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-csl-green/30 focus:border-csl-green transition-all bg-white appearance-none"
                    >
                      <option value="">{t('contact.form.subjectPlaceholder')}</option>
                      {SUBJECTS.map((s) => (
                        <option key={s} value={t(s)}>{t(s)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1.5">
                    {t('contact.form.message')} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={form.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    placeholder={t('contact.form.messagePlaceholder')}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-csl-green/30 focus:border-csl-green transition-all resize-none"
                  />
                </div>

                {/* Turnstile */}
                <div className="py-1">
                  <TurnstileWidget
                    ref={turnstileRef}
                    onSuccess={setTurnstileToken}
                    onExpire={() => setTurnstileToken('')}
                    onError={() => setTurnstileToken('')}
                  />
                </div>

                {/* Submit */}
                <button
                  type="submit"
                  disabled={isSubmitDisabled}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 bg-csl-green text-white font-semibold rounded-xl hover:bg-csl-dark-green transition-all duration-300 shadow-md hover:shadow-lg hover:scale-[1.01] active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:scale-100 text-sm sm:text-base"
                >
                  <PaperAirplaneIcon className="w-5 h-5" />
                  {t('contact.form.submit')}
                </button>

                {!turnstileToken && (
                  <p className="text-center text-xs text-gray-400">{t('contact.form.turnstileHint')}</p>
                )}
              </form>
            )}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA band ── */}
      <section className="py-12 sm:py-16 bg-gradient-to-br from-csl-green to-csl-dark-green relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            backgroundImage: 'radial-gradient(circle at 20% 50%, white 1px, transparent 1px), radial-gradient(circle at 80% 20%, white 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">{t('contact.cta.title')}</h2>
          <p className="text-white/80 mb-8 text-sm sm:text-base leading-relaxed">{t('contact.cta.description')}</p>
          <a
            href="https://wa.me/237653459638"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackEvent('Contact', { customData: { content_name: 'whatsapp-cta' } })}
            className="inline-flex items-center gap-2 px-8 py-4 bg-white text-csl-green font-bold rounded-xl hover:bg-gray-50 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105 active:scale-95 text-sm sm:text-base"
          >
            <PhoneIcon className="w-5 h-5" />
            {t('contact.cta.whatsapp')}
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;
